<template>
  <div class="tg-page-root tg-home-page relative min-h-screen bg-[#f8f7fb] dark:bg-gray-950">
    <!-- 右侧快速导航栏组件 -->
    <!-- 
    <ContinentNavigation
      :grouped-product-list="groupedProductList"
      :active-continent="activeContinent"
      :show-title="true"
      :default-expanded="false"
      @continent-click="scrollToContinent"
    />
    -->

    <!-- Main Content -->
    <div :class="['px-4 pb-6', showInstallBanner ? 'pb-28' : '']">
      <!-- Fixed Header Section -->
      <div
        ref="headerRef"
        class="tg-home-header sticky top-0 z-30 w-full px-4 pt-3 pb-2 bg-gray-50 dark:bg-gray-900/85 backdrop-blur-xl border-b-0"
      >
        <!-- Title Section -->
        <div class="mb-4 flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h1 class="text-3xl font-bold text-brand">
              {{ $t('LeSIM') }}
            </h1>
            <div class="w-16 h-1 bg-brand rounded-full mt-2"></div>
          </div>
          <div class="header-inline-actions flex-shrink-0">
            <TopRightActions mode="inline" :force-show="true" />
          </div>
        </div>

        <!-- Tab Navigation -->
        <div>
          <div class="home-tab-switcher flex rounded-2xl p-1.5">
            <button
              type="button"
              @click="switchTab('esim')"
              :class="['home-tab-btn', { 'home-tab-btn--active': activeTab === 'esim' }]"
            >
              {{ $t('Home_Tab_DataOnly') }}
            </button>
            <button
              type="button"
              @click="switchTab('simcard')"
              :class="['home-tab-btn', { 'home-tab-btn--active': activeTab === 'simcard' }]"
            >
              {{ $t('Home_Tab_NumberData') }}
            </button>
          </div>
        </div>

        <!-- Search -->
        <div class="mt-3">
          <div
            class="home-search-shell rounded-xl px-2.5 py-2"
            :class="isSearchFocused
              ? 'home-search-shell--focused'
              : 'home-search-shell--idle'"
          >
            <div class="relative">
              <span class="home-search-icon pointer-events-none absolute inset-y-0 left-0 z-[1] flex items-center pl-3 text-gray-400 dark:text-gray-500">
                <svg class="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                v-model.trim="searchKeyword"
                type="text"
                inputmode="search"
                enterkeyhint="search"
                autocomplete="off"
                autocorrect="off"
                spellcheck="false"
                :placeholder="$t('Search_countries') || 'Search countries/regions'"
                class="home-search-input"
                @focus="isSearchFocused = true"
                @blur="onSearchBlur"
              >
              <button
                v-if="searchKeyword"
                type="button"
                class="home-search-clear"
                @mousedown.prevent
                @click="searchKeyword = ''"
                :aria-label="$t('Clear') || 'Clear search'"
              >
                <svg class="home-search-clear-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M10 8.586l4.95-4.95a1 1 0 111.414 1.414L11.414 10l4.95 4.95a1 1 0 01-1.414 1.414L10 11.414l-4.95 4.95a1 1 0 01-1.414-1.414L8.586 10 3.636 5.05a1 1 0 011.414-1.414L10 8.586z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!isProductsReady" class="home-products-loading" aria-busy="true" :aria-label="$t('Orders_Loading')">
        <div v-for="n in 4" :key="n" class="home-skeleton-card">
          <div class="home-skeleton-flag" />
          <div class="home-skeleton-lines">
            <div class="home-skeleton-line home-skeleton-line--title" />
            <div class="home-skeleton-line home-skeleton-line--subtitle" />
          </div>
        </div>
      </div>

      <template v-else>
      <!-- eSIM Countries Section -->
      <div v-if="activeTab === 'esim' && hasVisibleProducts" class="mb-8">
        <!-- 按大洲分组显示 -->
        <div class="space-y-6">
          <template v-for="continent in orderedContinentOrder" :key="continent">
            <div v-if="groupedProductList[continent] && Object.keys(groupedProductList[continent]).length > 0"
              :id="`continent-${continent}`"
              class="scroll-mt-32">
              <!-- 大洲标题 -->
              <div class="home-continent-sticky-wrap mb-3">
                <div class="home-continent-sticky -mx-3 px-3 py-2">
                  <h3 class="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                    <span class="w-1.5 h-1.5 rounded-full bg-brand"></span>
                    {{ getContinentI18nName(continent) }}
                  </h3>
                </div>
              </div>
              
              <!-- 该大洲的国家列表 -->
              <div class="space-y-3">
                <div v-for="(products, country) in groupedProductList[continent]" :key="country"
                  @click="goToPackageView(country)" class="block cursor-pointer">
                  <div
                    class="home-country-card bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-5">
                    <div class="flex items-center justify-between">
                <div class="flex items-center gap-4">
                  <!-- Country Flag -->
                  <div class="relative w-16 h-12">
                    <!-- 国旗图标层 -->
                    <div 
                      :class="`flag-icon flag-icon-${getFlagCode(country).toLowerCase()}`"
                      class="absolute inset-0 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-100 dark:bg-gray-800 transition-opacity duration-200"
                      :data-country="country"
                      :style="{
                        'background-size': 'contain',
                        'background-position': 'center',
                        'background-repeat': 'no-repeat',
                        'z-index': flagError[country] ? '0' : '10',
                        'opacity': flagError[country] ? '0' : (flagLoading[country] ? '0.5' : '1')
                      }">
                    </div>
                    <!-- Loading 占位符 - 在检查期间显示 -->
                    <div 
                      v-if="flagLoading[country]"
                      class="absolute inset-0 w-16 h-12 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex items-center justify-center transition-opacity duration-200"
                      :style="{
                        'z-index': '5',
                        'opacity': flagLoading[country] ? '1' : '0'
                      }">
                      <div class="w-6 h-6 border-2 border-brand/30 border-t-brand rounded-full animate-spin"></div>
                    </div>
                    <!-- Fallback - 当国旗无效或加载失败时显示 -->
                    <div 
                      v-if="flagError[country]"
                      class="absolute inset-0 w-16 h-12 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 bg-brand flex items-center justify-center transition-opacity duration-200 overflow-hidden"
                      :style="{
                        'z-index': '10',
                        'opacity': flagError[country] ? '1' : '0'
                      }">
                      <span
                        v-if="isMultiRegionCode(country)"
                        class="text-white leading-none"
                        :style="{
                          'font-size': '1.15rem',
                          'text-shadow': '0 1px 2px rgba(0,0,0,0.25)',
                          'line-height': '1'
                        }">
                        🌐
                      </span>
                      <span 
                        v-else
                        class="text-white font-semibold text-center leading-none px-1"
                        :style="{
                          'font-size': '0.7rem',
                          'text-shadow': '0 1px 2px rgba(0,0,0,0.3)',
                          'line-height': '1'
                        }">
                        {{ formatFallbackText(country) }}
                      </span>
                    </div>
                  </div>

                  <!-- Country Info -->
                  <div class="flex-1">
                    <h3 class="home-country-title text-lg font-bold text-gray-800 dark:text-gray-100">
                      {{ displayCountryName(country) }}
                    </h3>
                    <p class="text-sm text-gray-500 dark:text-gray-300 mt-1 font-medium">
                      {{ $t('Available_packages') || 'Available packages' }}
                    </p>
                  </div>
                </div>

                <!-- Arrow Icon -->
                <div
                  class="home-country-arrow flex items-center justify-center w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-900">
                  <svg class="home-country-arrow-icon w-5 h-5 text-gray-400 dark:text-gray-300" fill="none"
                    stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
              </div>
            </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Regions Section -->
      <div v-if="tabPosition === 'regions'" class="mb-8">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-1 h-6 bg-brand rounded-full"></div>
          <h2 class="text-xl font-semibold text-gray-800 dark:text-gray-100">{{ $t('Regions') }}</h2>
        </div>
        <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
          <p class="text-gray-500 dark:text-gray-300 text-center">{{ $t('Regions_coming_soon') || 'Regions coming soon...' }}</p>
        </div>
      </div>

      <!-- SIM Card Section -->
      <div v-if="activeTab === 'simcard' && hasVisibleProducts" class="mb-8">
        <!-- 按大洲分组显示 -->
        <div class="space-y-6">
          <template v-for="continent in orderedContinentOrder" :key="continent">
            <div v-if="groupedProductList[continent] && Object.keys(groupedProductList[continent]).length > 0"
              :id="`continent-${continent}`"
              class="scroll-mt-32">
              <!-- 大洲标题 -->
              <div class="home-continent-sticky-wrap mb-3">
                <div class="home-continent-sticky -mx-3 px-3 py-2">
                  <h3 class="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                    <span class="w-1.5 h-1.5 rounded-full bg-brand"></span>
                    {{ getContinentI18nName(continent) }}
                  </h3>
                </div>
              </div>
              
              <!-- 该大洲的国家列表 -->
              <div class="space-y-3">
                <div v-for="(products, country) in groupedProductList[continent]" :key="country"
                  @click="goToPackageView(country, 'SIM')" class="block cursor-pointer">
                  <div
                    class="home-country-card bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-5">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-4">
                        <!-- Country Flag -->
                        <div class="relative w-16 h-12">
                          <!-- 国旗图标层 -->
                          <div 
                            :class="`flag-icon flag-icon-${getFlagCode(country).toLowerCase()}`"
                            class="absolute inset-0 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-100 dark:bg-gray-800 transition-opacity duration-200"
                            :data-country="country"
                            :style="{
                              'background-size': 'contain',
                              'background-position': 'center',
                              'background-repeat': 'no-repeat',
                              'z-index': flagError[country] ? '0' : '10',
                              'opacity': flagError[country] ? '0' : (flagLoading[country] ? '0.5' : '1')
                            }">
                          </div>
                          <!-- Loading 占位符 - 在检查期间显示 -->
                          <div 
                            v-if="flagLoading[country]"
                            class="absolute inset-0 w-16 h-12 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex items-center justify-center transition-opacity duration-200"
                            :style="{
                              'z-index': '5',
                              'opacity': flagLoading[country] ? '1' : '0'
                            }">
                            <div class="w-6 h-6 border-2 border-brand/30 border-t-brand rounded-full animate-spin"></div>
                          </div>
                          <!-- Fallback - 当国旗无效或加载失败时显示 -->
                          <div 
                            v-if="flagError[country]"
                            class="absolute inset-0 w-16 h-12 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 bg-brand flex items-center justify-center transition-opacity duration-200 overflow-hidden"
                            :style="{
                              'z-index': '10',
                              'opacity': flagError[country] ? '1' : '0'
                            }">
                            <span
                              v-if="isMultiRegionCode(country)"
                              class="text-white leading-none"
                              :style="{
                                'font-size': '1.15rem',
                                'text-shadow': '0 1px 2px rgba(0,0,0,0.25)',
                                'line-height': '1'
                              }">
                              🌐
                            </span>
                            <span 
                              v-else
                              class="text-white font-semibold text-center leading-none px-1"
                              :style="{
                                'font-size': '0.7rem',
                                'text-shadow': '0 1px 2px rgba(0,0,0,0.3)',
                                'line-height': '1'
                              }">
                              {{ formatFallbackText(country) }}
                            </span>
                          </div>
                        </div>

                        <!-- Country Info -->
                        <div class="flex-1">
                          <h3 class="home-country-title text-lg font-bold text-gray-800 dark:text-gray-100">
                            {{ displayCountryName(country) }}
                          </h3>
                          <p class="text-sm text-gray-500 dark:text-gray-300 mt-1 font-medium">
                            {{ $t('Available_packages') || 'Available packages' }}
                          </p>
                        </div>
                      </div>

                      <!-- Arrow Icon -->
                      <div
                        class="home-country-arrow flex items-center justify-center w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-900">
                        <svg class="home-country-arrow-icon w-5 h-5 text-gray-400 dark:text-gray-300" fill="none"
                          stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div
        v-if="!hasVisibleProducts"
        class="home-empty-state flex flex-col items-center justify-center py-12 px-4"
      >
        <div
          class="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 rounded-full flex items-center justify-center mb-4">
          <svg class="w-10 h-10 text-gray-400 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-700 dark:text-gray-300 mb-1">{{ $t('No_countries_found') || 'No countries found' }}</h3>
        <p class="text-sm text-gray-500 dark:text-gray-300 text-center">{{ $t('Try_different_search') || 'Try a different search term' }}</p>
      </div>

      <footer class="home-company-footer">
        <div class="home-footer-accent" aria-hidden="true"></div>
        <nav class="home-footer-nav" :aria-label="$t('Leen_Footer_Label_Service')">
          <template v-for="(link, index) in footerLinks" :key="link.labelKey">
            <span v-if="index > 0" class="home-footer-sep" aria-hidden="true">·</span>
            <router-link :to="link.to" class="home-footer-link">
              {{ $t(link.labelKey) }}
            </router-link>
          </template>
        </nav>
        <p class="home-footer-meta">
          {{ $t('Contact_Company_Name') }} · © {{ copyrightYear }}
        </p>
      </footer>
      </template>
    </div>

    <!-- Campaign Poster Dialog -->
    <transition name="fade">
      <div
        v-if="showCampaignPosterDialog"
        class="fixed inset-0 z-[130] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        @click.self="closeCampaignPosterDialog"
      >
        <div class="w-full max-w-[380px] max-h-[90vh] rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden border border-white/30 flex flex-col campaign-dialog-panel">
          <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-r from-violet-50 to-white">
            <div>
              <h3 class="text-sm font-bold text-gray-900 dark:text-gray-100">{{ $t('Campaign_Poster_Title') }}</h3>
              <p class="text-[11px] text-gray-500 dark:text-gray-300">{{ $t('Campaign_Poster_Subtitle') }}</p>
            </div>
            <button
              type="button"
              class="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 active:bg-gray-200 dark:active:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors duration-150"
              @click="closeCampaignPosterDialog"
              aria-label="close"
            >
              ×
            </button>
          </div>
          <div class="p-3 bg-gray-50 dark:bg-gray-900 flex-1 overflow-y-auto campaign-poster-scroll">
            <img
              :src="campaignPosterSrc"
              alt="campaign poster"
              class="w-full rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm object-contain"
            />
          </div>
          <div class="campaign-footer px-4 py-3 flex items-center justify-between gap-2">
            <div class="campaign-footer-spacer"></div>
            <button
              type="button"
              class="campaign-btn campaign-btn-primary"
              @click="closeCampaignPosterDialog"
            >
              {{ $t('Campaign_Poster_Got_It') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="showInstallGuideDialog"
        class="fixed inset-0 z-[135] bg-black/55 backdrop-blur-[1px] flex items-center justify-center p-4"
        @click.self="closeInstallGuideDialog"
      >
        <div class="w-full max-w-[380px] rounded-3xl bg-white dark:bg-gray-900 overflow-hidden shadow-2xl install-guide-dialog">
          <div class="install-guide-header bg-brand">
            <h3 class="install-guide-title">{{ $t('Install_Guide_Title') }}</h3>
            <button
              type="button"
              class="install-guide-close"
              aria-label="close"
              @click="closeInstallGuideDialog"
            >
              ×
            </button>
          </div>
          <div class="install-guide-content">
            <div class="install-guide-grid">
              <div class="install-guide-step">
                <div class="install-guide-media">
                  <img :src="installGuideImages[0]" alt="step 1" class="install-guide-image" />
                  <span class="install-guide-index">01</span>
                </div>
                <p class="install-guide-caption">{{ $t('Install_Guide_Step_1') }}</p>
              </div>
              <div class="install-guide-step">
                <div class="install-guide-media">
                  <img :src="installGuideImages[1]" alt="step 2" class="install-guide-image" />
                  <span class="install-guide-index">02</span>
                </div>
                <p class="install-guide-caption">{{ $t('Install_Guide_Step_2') }}</p>
              </div>
              <div class="install-guide-step">
                <div class="install-guide-media">
                  <img :src="installGuideImages[2]" alt="step 3" class="install-guide-image" />
                  <span class="install-guide-index">03</span>
                </div>
                <p class="install-guide-caption">{{ $t('Install_Guide_Step_3') }}</p>
              </div>
              <div class="install-guide-step">
                <div class="install-guide-media">
                  <img :src="installGuideImages[3]" alt="step 4" class="install-guide-image" />
                  <span class="install-guide-index">04</span>
                </div>
                <p class="install-guide-caption">{{ $t('Install_Guide_Step_4') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <ScrollToTopButton :extra-bottom="showInstallBanner ? 76 : 0" />

    <div
      v-if="showInstallBanner"
      class="mobile-frame-fixed tg-bottom-bar install-banner-wrap fixed bottom-0 left-0 right-0 z-[120] pb-2"
    >
      <div class="install-banner">
        <div class="install-banner-icon">
          <img src="/favicon.ico" alt="LeSIM logo" class="install-banner-logo" />
        </div>
        <div class="install-banner-content">
          <div class="install-banner-title">{{ $t('Install_Banner_Title') }}</div>
          <div class="install-banner-subtitle">{{ $t('Install_Banner_Subtitle') }}</div>
        </div>
        <div class="install-banner-actions">
          <button type="button" class="install-btn install-btn-primary bg-brand active:bg-brand/90 active:scale-[0.98] transition-[transform,background-color] duration-150" @click="openInstallGuideDialog">{{ $t('Install_Banner_Add') }}</button>
          <button type="button" class="install-btn install-btn-ghost active:scale-[0.98] transition-transform duration-150" @click="closeInstallBanner">{{ $t('Install_Banner_Close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, onActivated, onDeactivated, nextTick, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { showAppNotification } from '@/utils/appNotification'
import { useI18n } from 'vue-i18n'
import logger from '@/utils/logger'
import TopRightActions from '@/components/TopRightActions.vue'
import ScrollToTopButton from '@/components/ScrollToTopButton.vue'
import { groupCountriesByContinent, getContinentDisplayName, getContinentOrder } from '@/utils/countryToContinent'
import {
  getOrderedContinents,
  getUserCountryHint,
  getUserCountryHintDebug,
  logUserCountry,
  prioritizeUserCountryInGrouped,
  resolveUserCountryKey,
} from '@/utils/userCountryHint'
import { getCountryRegionDisplayName, getFlagCountryCode } from '@/utils/countryRegionDisplay'
import { resetPageScroll } from '@/utils/scrollReset'
import { useTelegramWebApp } from '@/composables/useTelegramWebApp'
import { bindTelegramEvent, unbindTelegramEvent } from '@/utils/telegramWebAppClient'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import { getTelegramCloudItem, setTelegramCloudItem } from '@/utils/telegramCloudStorage'
import { isTelegramWebAppActive } from '@/utils/telegramWebAppClient'
import guideStep1 from '@/assets/img/guide/guide-step-1.png'
import guideStep2 from '@/assets/img/guide/guide-step-2.png'
import guideStep3 from '@/assets/img/guide/guide-step-3.png'
import guideStep4 from '@/assets/img/guide/guide-step-4.png'
import guideEnStep2 from '@/assets/img/guide/guide-en-step-2.png'
import guideEnStep3 from '@/assets/img/guide/guide-en-step-3.png'
import guideEnStep4 from '@/assets/img/guide/guide-en-step-4.png'
// import ContinentNavigation from '@/components/ContinentNavigation.vue'

const { t, te, locale } = useI18n();
const copyrightYear = new Date().getFullYear()
const footerLinks = [
  { to: { name: 'LegalPolicyView', params: { policyType: 'terms' } }, labelKey: 'PageTitle_Terms' },
  { to: { name: 'LegalPolicyView', params: { policyType: 'privacy' } }, labelKey: 'PageTitle_Privacy' },
  { to: { name: 'ReturnPolicyView' }, labelKey: 'PageTitle_ReturnPolicy' },
  { to: { name: 'ContactView' }, labelKey: 'PageTitle_Contact' },
]
const route = useRoute()
const router = useRouter()
const esimProductList = ref({})  // eSIM产品列表（不带电话号码）
const simProductList = ref({})   // SIM产品列表（带电话号码）
// flagError: true 表示显示 fallback，false/null/undefined 表示显示国旗
// 默认假设国旗存在，只在确认加载失败时才显示 fallback
const flagError = ref({})
// 跟踪正在加载的国旗
const flagLoading = ref({})
const tabPosition = ref('countries') // 添加缺失的变量
const activeTab = ref('esim') // 默认选中eSIM tab
const isProductsReady = ref(false)
const searchKeyword = ref('')
const isSearchFocused = ref(false)
const detectedCountryHint = ref(null)

const BODY_CLASS_TG_HOME = 'tg-home-route'
let continentStickyRaf = 0

const logUserCountryState = (trigger) => {
  const grouped = groupedProductList.value || {}
  const firstContinent = orderedContinentOrder.value[0] || null
  const firstCountry = firstContinent
    ? Object.keys(grouped[firstContinent] || {})[0] || null
    : null

  logUserCountry(`state @ ${trigger}`, {
    tab: activeTab.value,
    searchActive: Boolean(String(searchKeyword.value || '').trim()),
    detectedHint: detectedCountryHint.value,
    esimUserCountryKey: esimUserCountryKey.value,
    simUserCountryKey: simUserCountryKey.value,
    activeUserCountryKey: userCountryKey.value,
    orderedContinents: orderedContinentOrder.value,
    firstContinent,
    firstCountry,
    esimProductCount: Object.keys(esimFilteredProductList.value || {}).length,
    simProductCount: Object.keys(simFilteredProductList.value || {}).length,
  })
}

const syncHeaderHeight = () => {
  if (!headerRef.value) return
  headerHeight.value = Math.round(headerRef.value.getBoundingClientRect().height)
  const stickyTop = `${headerHeight.value}px`
  document.documentElement.style.setProperty('--home-header-spacer-height', stickyTop)
  document.documentElement.style.setProperty('--home-continent-sticky-top', stickyTop)
  updateContinentStickyFallback()
}

const shouldUseContinentStickyFallback = () => (
  document.body.classList.contains('tg-home-route')
)

const getContinentPinMetrics = () => {
  const root = document.documentElement
  const styles = getComputedStyle(root)

  if (document.body.classList.contains('tg-fullscreen')) {
    const safeLeft = Number.parseFloat(styles.getPropertyValue('--tg-content-safe-left')) || 0
    const safeRight = Number.parseFloat(styles.getPropertyValue('--tg-content-safe-right')) || 0
    return {
      left: `${safeLeft}px`,
      width: `calc(100% - ${safeLeft + safeRight}px)`,
    }
  }

  return {
    left: styles.getPropertyValue('--app-left').trim() || '0px',
    width: styles.getPropertyValue('--app-width').trim() || '100%',
  }
}

const clearContinentStickyPins = () => {
  document.querySelectorAll('.home-continent-sticky').forEach((el) => {
    el.classList.remove('home-continent-sticky--pinned')
    el.style.removeProperty('left')
    el.style.removeProperty('width')
    const wrap = el.closest('.home-continent-sticky-wrap')
    if (wrap) wrap.style.minHeight = ''
  })
}

const updateContinentStickyFallback = () => {
  if (!shouldUseContinentStickyFallback()) {
    clearContinentStickyPins()
    return
  }

  const top = headerHeight.value
  const { left: pinLeft, width: pinWidth } = getContinentPinMetrics()
  const scrollY = window.scrollY || document.documentElement.scrollTop || 0
  const anchor = scrollY + top

  // Pin the last section whose top has crossed the header anchor (current scroll group).
  let activePin = null

  document.querySelectorAll('.home-continent-sticky').forEach((el) => {
    const section = el.closest('.scroll-mt-32')
    const wrap = el.closest('.home-continent-sticky-wrap')
    if (!section || !wrap) return

    const sectionRect = section.getBoundingClientRect()
    const sectionTop = sectionRect.top + scrollY
    const sectionBottom = sectionTop + section.offsetHeight
    const barHeight = el.offsetHeight
    const isCurrentGroup = sectionTop <= anchor + 1 && sectionBottom > anchor + barHeight + 1

    if (isCurrentGroup) {
      activePin = { el, wrap, barHeight }
    }
  })

  document.querySelectorAll('.home-continent-sticky').forEach((el) => {
    const wrap = el.closest('.home-continent-sticky-wrap')
    if (!wrap) return

    if (activePin?.el === el) {
      el.classList.add('home-continent-sticky--pinned')
      el.style.left = pinLeft
      el.style.width = pinWidth
      wrap.style.minHeight = `${activePin.barHeight}px`
      return
    }

    el.classList.remove('home-continent-sticky--pinned')
    el.style.removeProperty('left')
    el.style.removeProperty('width')
    wrap.style.minHeight = ''
  })
}

const scheduleContinentStickyFallback = () => {
  if (continentStickyRaf) cancelAnimationFrame(continentStickyRaf)
  continentStickyRaf = requestAnimationFrame(() => {
    continentStickyRaf = 0
    updateContinentStickyFallback()
  })
}

const onSearchBlur = () => {
  isSearchFocused.value = false
  hideKeyboard()
  requestAnimationFrame(syncHeaderHeight)
}
const headerRef = ref(null)
const headerHeight = ref(130) // 默认高度，会根据实际高度更新
const showCampaignPosterDialog = ref(false)
const campaignPosterSrc = ref('/campaign/marketing-email/en-poster.png')
const showInstallBanner = ref(false)
const showInstallGuideDialog = ref(false)
const installGuideImages = computed(() => {
  const currentLocale = String(locale?.value || '').toLowerCase()
  if (currentLocale.startsWith('en')) {
    return [guideStep1, guideEnStep2, guideEnStep3, guideEnStep4]
  }
  return [guideStep1, guideStep2, guideStep3, guideStep4]
})
const INSTALL_BANNER_VERSION = 'v1'
const INSTALL_BANNER_DAY_MS = 24 * 60 * 60 * 1000
const INSTALL_BANNER_DISMISS_DAYS = 7
const INSTALL_BANNER_GUIDE_DAYS = 30
const installBannerStorageKey = (name) => `install_banner_${name}_${INSTALL_BANNER_VERSION}`

const getTodayKey = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const readStorageNumber = async (key) => {
  const raw = await getTelegramCloudItem(key, '0')
  const num = Number(raw)
  return Number.isFinite(num) ? num : 0
}

const isStandaloneMode = () => {
  return window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator?.standalone === true
}

const isLikelySafari = () => {
  const ua = String(navigator.userAgent || '')
  return /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|Edg|EdgiOS|OPR|Opera|SamsungBrowser/i.test(ua)
}

const shouldShowInstallBanner = async () => {
  if (route.path !== '/') {
    return false
  }
  if (isTelegramWebAppActive()) {
    return false
  }
  if (isStandaloneMode() || isLikelySafari()) {
    return false
  }

  const now = Date.now()
  const dismissedUntil = await readStorageNumber(installBannerStorageKey('dismissed_until'))
  const guideOpenedUntil = await readStorageNumber(installBannerStorageKey('guide_opened_until'))
  const lastShownDay = await getTelegramCloudItem(installBannerStorageKey('last_shown_day'), '')

  if (dismissedUntil > now || guideOpenedUntil > now) {
    return false
  }
  if (lastShownDay === getTodayKey()) {
    return false
  }
  return true
}

const evaluateInstallBanner = async () => {
  const visible = await shouldShowInstallBanner()
  showInstallBanner.value = visible
  if (visible) {
    await setTelegramCloudItem(installBannerStorageKey('last_shown_day'), getTodayKey())
  }
}

const filterProductListByKeyword = (list) => {
  const keyword = String(searchKeyword.value || '').trim().toLowerCase()
  if (!keyword) {
    return list || {}
  }

  return Object.entries(list || {}).reduce((acc, [country, products]) => {
    const countryCode = String(country || '').toLowerCase()
    const displayName = String(displayCountryName(country) || '').toLowerCase()
    if (countryCode.includes(keyword) || displayName.includes(keyword)) {
      acc[country] = products
    }
    return acc
  }, {})
}

const esimFilteredProductList = computed(() => filterProductListByKeyword(esimProductList.value))
const simFilteredProductList = computed(() => filterProductListByKeyword(simProductList.value))

const productList = computed(() => (
  activeTab.value === 'esim' ? esimProductList.value : simProductList.value
))

const filteredProductList = computed(() => (
  activeTab.value === 'esim' ? esimFilteredProductList.value : simFilteredProductList.value
))

const esimUserCountryKey = computed(() => {
  if (String(searchKeyword.value || '').trim()) return null
  return resolveUserCountryKey(detectedCountryHint.value, esimFilteredProductList.value)
})

const simUserCountryKey = computed(() => {
  if (String(searchKeyword.value || '').trim()) return null
  return resolveUserCountryKey(detectedCountryHint.value, simFilteredProductList.value)
})

const userCountryKey = computed(() => (
  activeTab.value === 'esim' ? esimUserCountryKey.value : simUserCountryKey.value
))

const groupedProductList = computed(() => {
  const grouped = groupCountriesByContinent(filteredProductList.value)
  return prioritizeUserCountryInGrouped(grouped, userCountryKey.value)
})

const baseContinentOrder = getContinentOrder()

const orderedContinentOrder = computed(() => (
  getOrderedContinents(baseContinentOrder, groupedProductList.value, userCountryKey.value)
))

const availableContinents = computed(() => orderedContinentOrder.value)

const hasVisibleProducts = computed(() => availableContinents.value.length > 0)

// 当前激活的大洲（用于高亮显示）
const activeContinent = ref(null)

// 获取大洲的i18n名称（用于页面标题显示）
const getContinentI18nName = (continentCode) => {
  const i18nKeys = {
    'asia': 'Asia',
    'europe': 'Europe',
    'north-america': 'North_America',
    'central-america': 'Central_America',
    'caribbean': 'Caribbean',
    'south-america': 'South_America',
    'africa': 'Africa',
    'oceania': 'Oceania',
    'multi-region': 'Multi_Region'
  }
  const key = i18nKeys[continentCode] || continentCode
  // 尝试翻译，如果不存在则使用英文名称
  if (te && te(key)) {
    return t(key)
  }
  // 回退到显示名称
  return getContinentDisplayName(continentCode)
}

// 自定义平滑滚动函数
const smoothScrollTo = (targetPosition) => {
  const startPosition = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0
  const distance = targetPosition - startPosition
  const duration = 500 // 滚动持续时间（毫秒）
  let start = null
  
  const step = (timestamp) => {
    if (!start) start = timestamp
    const progress = timestamp - start
    const percentage = Math.min(progress / duration, 1)
    
    // 使用缓动函数（easeInOutCubic）
    const easeInOutCubic = percentage < 0.5
      ? 4 * percentage * percentage * percentage
      : 1 - Math.pow(-2 * percentage + 2, 3) / 2
    
    const currentPosition = startPosition + distance * easeInOutCubic
    window.scrollTo(0, currentPosition)
    
    if (percentage < 1) {
      window.requestAnimationFrame(step)
    }
  }
  
  window.requestAnimationFrame(step)
}

// 滚动到大洲
const scrollToContinent = (continent) => {
  logger.debug('scrollToContinent called with:', continent)
  
  // 确保continent是字符串
  const continentStr = String(continent)
  const elementId = `continent-${continentStr}`
  logger.debug('Looking for element with ID:', elementId)
  
  hapticFeedback('impact', 'medium')
  
  // 使用nextTick确保DOM已更新，然后尝试查找元素
  nextTick(() => {
    // 尝试多种方式查找元素
    let element = document.getElementById(elementId)
    
    // 如果直接查找失败，尝试查找所有可能的ID格式
    if (!element) {
      logger.debug('Element not found with ID:', elementId, 'Trying alternative methods...')
      
      // 尝试查找所有包含continent的ID
      const allElements = document.querySelectorAll('[id^="continent-"]')
      logger.debug('Found elements with continent- prefix:', allElements.length)
      
      // 尝试查找匹配的continent
      for (const el of allElements) {
        const id = el.id
        const continentFromId = id.replace('continent-', '')
        logger.debug('Checking element:', id, 'continent:', continentFromId, 'matches:', continentFromId === continentStr)
        if (continentFromId === continentStr) {
          element = el
          logger.debug('Found matching element:', element)
          break
        }
      }
    }
    
    if (element) {
      logger.debug('Element found:', element, 'ID:', element.id)
      
      // 立即更新激活的大洲（提供即时反馈）
      activeContinent.value = continentStr
      
      // 计算目标位置（考虑固定头部等）
      const rect = element.getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0
      const elementTop = rect.top + scrollTop
      const offset = 120 // 偏移量，避免被头部遮挡
      const targetPosition = Math.max(0, elementTop - offset)
      
      logger.debug('Scrolling to:', {
        targetPosition,
        elementTop,
        scrollTop,
        rectTop: rect.top,
        offset,
        windowHeight: window.innerHeight
      })
      
      // 优先使用 scrollIntoView，然后调整偏移
      try {
        // 先使用 scrollIntoView 滚动到元素
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        })
        
        // 等待滚动开始后，调整偏移量
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const currentScroll = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0
            const newRect = element.getBoundingClientRect()
            const newElementTop = newRect.top + currentScroll
            const adjustedTarget = Math.max(0, newElementTop - offset)
            
            const diff = Math.abs(currentScroll - adjustedTarget)
            if (diff > 20) {
              logger.debug('Adjusting scroll position. Current:', currentScroll, 'Target:', adjustedTarget, 'Diff:', diff)
              // 使用自定义平滑滚动进行微调
              smoothScrollTo(adjustedTarget)
            }
          })
        })
      } catch (e) {
        logger.warn('scrollIntoView failed, using custom smooth scroll:', e)
        // 备用方法：直接使用自定义平滑滚动
        smoothScrollTo(targetPosition)
      }
    } else {
      logger.warn(`Element with id '${elementId}' not found. Available continent IDs:`, 
        Array.from(document.querySelectorAll('[id^="continent-"]')).map(el => el.id))
      
      // 如果找不到元素，尝试延迟重试一次
      setTimeout(() => {
        const retryElement = document.getElementById(elementId)
        if (retryElement) {
          logger.debug('Found element on retry:', retryElement)
          activeContinent.value = continentStr
          
          try {
            retryElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest'
            })
            
            // 调整偏移量
            setTimeout(() => {
              const rect = retryElement.getBoundingClientRect()
              const scrollTop = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0
              const elementTop = rect.top + scrollTop
              const offset = 120
              const targetPosition = Math.max(0, elementTop - offset)
              const currentScroll = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0
              const diff = Math.abs(currentScroll - targetPosition)
              
              if (diff > 30) {
                smoothScrollTo(targetPosition)
              }
            }, 100)
          } catch (e) {
            logger.warn('Retry scrollIntoView failed, using manual scroll:', e)
            const rect = retryElement.getBoundingClientRect()
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0
            const elementTop = rect.top + scrollTop
            const offset = 120
            const targetPosition = Math.max(0, elementTop - offset)
            smoothScrollTo(targetPosition)
          }
        } else {
          logger.error(`Element with id '${elementId}' still not found after retry`)
        }
      }, 300)
    }
  })
}

// 防抖函数
const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

const handleContinentStickyScroll = () => {
  scheduleContinentStickyFallback()
}

const handleTgAppFrameSync = () => {
  requestAnimationFrame(() => {
    syncHeaderHeight()
    scheduleContinentStickyFallback()
  })
}

// 监听滚动，更新激活的大洲（使用防抖优化性能）
const handleScroll = debounce(() => {
  const scrollPosition = window.scrollY + 150 // 偏移量
  
  // 检查每个大洲的位置
  for (let i = availableContinents.value.length - 1; i >= 0; i--) {
    const continent = availableContinents.value[i]
    const element = document.getElementById(`continent-${continent}`)
    if (element) {
      const rect = element.getBoundingClientRect()
      const elementTop = rect.top + window.scrollY
      if (scrollPosition >= elementTop - 50) {
        if (activeContinent.value !== continent) {
          activeContinent.value = continent
        }
        break
      }
    }
  }
}, 100) // 100ms 防抖

// 格式化 fallback 文字：对于长国家代码，只显示前几个字符
const formatFallbackText = (country) => {
  if (!country) return ''
  // 如果国家代码长度 <= 5，显示完整
  if (country.length <= 5) {
    return country
  }
  // 如果包含连字符，取前两个部分
  if (country.includes('-')) {
    const parts = country.split('-')
    if (parts.length >= 2) {
      return parts.slice(0, 2).join('-')
    }
  }
  // 否则只显示前4个字符
  return country.substring(0, 4)
}


const { hapticFeedback, hideKeyboard } = useTelegramWebApp()

let resizeObserver = null;
let tgViewportSyncHandler = null
const BODY_CLASS_CAMPAIGN_DIALOG_OPEN = 'campaign-dialog-open'

const getCampaignPosterSrc = () => {
  const posterQuery = String(route.query.poster || route.query.campaignPoster || route.query.campaign || '').toLowerCase()
  const langQuery = String(route.query.lang || '').toLowerCase()
  const localeLang = String(locale?.value || '').toLowerCase()

  // Supported triggers:
  // /?poster=en
  // /?poster=zh
  // /?campaign=appreciation-week&lang=zh
  if (!posterQuery) {
    return ''
  }

  if (posterQuery === 'zh' || posterQuery === 'zh-cn' || posterQuery === 'zh-hk') {
    return '/campaign/marketing-email/zh-poster.png'
  }
  if (posterQuery === 'en') {
    return '/campaign/marketing-email/en-poster.png'
  }
  if (posterQuery === 'appreciation-week' || posterQuery === 'campaign' || posterQuery === 'promo') {
    const shouldUseZh = langQuery.startsWith('zh') || localeLang.startsWith('zh')
    return shouldUseZh ? '/campaign/marketing-email/zh-poster.png' : '/campaign/marketing-email/en-poster.png'
  }

  return ''
}

const maybeShowCampaignPoster = () => {
  if (route.path !== '/') {
    return
  }
  const campaignQuery = String(route.query.campaign || '').trim().toLowerCase()
  // Global dialog in App.vue already handles appreciation-week campaign.
  if (campaignQuery === 'appreciation-week') {
    showCampaignPosterDialog.value = false
    return
  }
  const src = getCampaignPosterSrc()
  if (!src) {
    return
  }
  campaignPosterSrc.value = src
  showCampaignPosterDialog.value = true
}

const closeCampaignPosterDialog = () => {
  showCampaignPosterDialog.value = false
}

const closeInstallBanner = async () => {
  showInstallBanner.value = false
  const until = Date.now() + INSTALL_BANNER_DISMISS_DAYS * INSTALL_BANNER_DAY_MS
  await setTelegramCloudItem(installBannerStorageKey('dismissed_until'), String(until))
}

const openInstallGuideDialog = async () => {
  const until = Date.now() + INSTALL_BANNER_GUIDE_DAYS * INSTALL_BANNER_DAY_MS
  await setTelegramCloudItem(installBannerStorageKey('guide_opened_until'), String(until))
  showInstallBanner.value = false
  showInstallGuideDialog.value = true
}

const closeInstallGuideDialog = () => {
  showInstallGuideDialog.value = false
}

const bindHomeRouteClass = () => {
  if (route.path === '/') {
    document.body.classList.add(BODY_CLASS_TG_HOME)
    document.documentElement.classList.add(BODY_CLASS_TG_HOME)
    return
  }
  unbindHomeRouteClass()
}

const unbindHomeRouteClass = () => {
  document.body.classList.remove(BODY_CLASS_TG_HOME)
  document.documentElement.classList.remove(BODY_CLASS_TG_HOME)
  clearContinentStickyPins()
}

onActivated(() => {
  bindHomeRouteClass()
  if (!isProductsReady.value) {
    loadProductList()
  }
  requestAnimationFrame(() => {
    syncHeaderHeight()
    scheduleContinentStickyFallback()
  })
})

onDeactivated(() => {
  unbindHomeRouteClass()
})

onMounted(async () => {
  bindHomeRouteClass()
  const hintDebug = getUserCountryHintDebug()
  detectedCountryHint.value = hintDebug.hint || getUserCountryHint()
  logUserCountry('detected on mount', hintDebug)
  const savedTab = await getTelegramCloudItem(STORAGE_KEYS.HOME_ACTIVE_TAB)
  if (savedTab === 'esim' || savedTab === 'simcard') {
    activeTab.value = savedTab
  }

  paymentMessage();
  loadProductList();
  maybeShowCampaignPoster();
  await evaluateInstallBanner();
  
  const remeasureHeader = () => {
    if (isSearchFocused.value) return
    syncHeaderHeight()
  }

  // 初始化头部高度
  if (headerRef.value) {
    syncHeaderHeight()
    
    // 监听头部高度变化
    resizeObserver = new ResizeObserver(() => {
      if (isSearchFocused.value) return
      syncHeaderHeight()
    });
    resizeObserver.observe(headerRef.value);
  }
  
  // Scroll to top when page loads (Telegram Mini App compatible)
  scrollToTop()
  
  // 监听滚动事件
  window.addEventListener('scroll', handleContinentStickyScroll, { passive: true })
  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('tg-app-frame-sync', handleTgAppFrameSync)
  window.addEventListener('storage', onProductListStorageUpdate)
  scheduleContinentStickyFallback()

  tgViewportSyncHandler = () => {
    requestAnimationFrame(() => {
      syncHeaderHeight()
      scheduleContinentStickyFallback()
    })
  }
  bindTelegramEvent('viewportChanged', tgViewportSyncHandler)
  bindTelegramEvent('fullscreenChanged', tgViewportSyncHandler)
  bindTelegramEvent('safeAreaChanged', tgViewportSyncHandler)
  bindTelegramEvent('contentSafeAreaChanged', tgViewportSyncHandler)
});

watch(
  () => route.fullPath,
  () => {
    maybeShowCampaignPoster()
    evaluateInstallBanner()
  }
)

watch(activeTab, () => {
  logUserCountryState('tab-change')
  nextTick(() => {
    syncHeaderHeight()
    scheduleContinentStickyFallback()
  })
})

watch(searchKeyword, () => {
  logUserCountryState('search-change')
})

watch([
  groupedProductList,
  orderedContinentOrder,
  userCountryKey,
  esimUserCountryKey,
  simUserCountryKey,
  esimProductList,
  simProductList,
  hasVisibleProducts,
], () => {
  logUserCountryState('data-change')
  nextTick(() => {
    syncHeaderHeight()
    scheduleContinentStickyFallback()
  })
})

watch(showCampaignPosterDialog, (visible) => {
  if (visible) {
    document.body.classList.add(BODY_CLASS_CAMPAIGN_DIALOG_OPEN)
    return
  }
  document.body.classList.remove(BODY_CLASS_CAMPAIGN_DIALOG_OPEN)
})

onUnmounted(() => {
  // 移除滚动监听
  window.removeEventListener('scroll', handleContinentStickyScroll)
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('tg-app-frame-sync', handleTgAppFrameSync)
  window.removeEventListener('storage', onProductListStorageUpdate)
  unbindHomeRouteClass()
  document.body.classList.remove(BODY_CLASS_CAMPAIGN_DIALOG_OPEN)
  if (continentStickyRaf) {
    cancelAnimationFrame(continentStickyRaf)
    continentStickyRaf = 0
  }
  
  if (resizeObserver) {
    resizeObserver.disconnect();
  }

  if (tgViewportSyncHandler) {
    unbindTelegramEvent('viewportChanged', tgViewportSyncHandler)
    unbindTelegramEvent('fullscreenChanged', tgViewportSyncHandler)
    unbindTelegramEvent('safeAreaChanged', tgViewportSyncHandler)
    unbindTelegramEvent('contentSafeAreaChanged', tgViewportSyncHandler)
    tgViewportSyncHandler = null
  }
});

const initFlagStateForCountries = (countries = []) => {
  countries.forEach((country) => {
    if (flagError.value[country] === undefined) {
      flagError.value[country] = false
      flagLoading.value[country] = true
    }
  })
}

const applyProductListFromStorage = (storageKey, targetRef) => {
  const listStr = localStorage.getItem(storageKey)
  if (!listStr) return false

  try {
    const parsed = JSON.parse(listStr)
    targetRef.value = parsed
    initFlagStateForCountries(Object.keys(parsed))
    return true
  } catch (error) {
    logger.error(`Error parsing ${storageKey}:`, error)
    return true
  }
}

const refreshProductListsFromStorage = () => {
  const esimReady = applyProductListFromStorage(STORAGE_KEYS.PRODUCT_LIST, esimProductList)
  const simReady = applyProductListFromStorage(STORAGE_KEYS.SIM_PRODUCT_LIST, simProductList)
  if (esimReady || simReady) {
    preloadAllFlags()
  }
  return { esimReady, simReady }
}

const onProductListStorageUpdate = (event) => {
  if (event.key !== STORAGE_KEYS.PRODUCT_LIST && event.key !== STORAGE_KEYS.SIM_PRODUCT_LIST) {
    return
  }
  refreshProductListsFromStorage()
}

const hasStoredProductData = () => (
  Object.keys(esimProductList.value || {}).length > 0
  || Object.keys(simProductList.value || {}).length > 0
)

const finishProductListLoad = (reason) => {
  isProductsReady.value = true
  preloadAllFlags()
  nextTick(() => {
    logUserCountryState(reason)
  })
}

const loadProductList = () => {
  let esimLoaded = false
  let simLoaded = false
  let pollAttempts = 0
  const MAX_POLL_ATTEMPTS = 20

  const tryLoad = () => {
    if (!esimLoaded) {
      esimLoaded = applyProductListFromStorage(STORAGE_KEYS.PRODUCT_LIST, esimProductList)
      if (esimLoaded) {
        logger.debug('esimProductList.value', esimProductList.value)
      }
    }

    if (!simLoaded) {
      simLoaded = applyProductListFromStorage(STORAGE_KEYS.SIM_PRODUCT_LIST, simProductList)
      if (simLoaded) {
        logger.debug('simProductList.value', simProductList.value)
      }
    }

    return esimLoaded && simLoaded
  }

  if (tryLoad()) {
    finishProductListLoad('products-loaded')
    return
  }

  if (hasStoredProductData()) {
    finishProductListLoad('products-partial-loaded')
    return
  }

  const intervalId = setInterval(() => {
    pollAttempts += 1

    if (tryLoad() || hasStoredProductData() || pollAttempts >= MAX_POLL_ATTEMPTS) {
      clearInterval(intervalId)
      finishProductListLoad(
        tryLoad() ? 'products-loaded' : (hasStoredProductData() ? 'products-partial-loaded' : 'products-poll-timeout')
      )
    }
  }, 300)
}

// 检查国旗是否有效（简化版本，主要依赖 flagError 状态）
const hasValidFlag = (countryCode) => {
  if (!countryCode) return false
  // 如果已经标记为错误，返回 false（显示 fallback）
  return !flagError.value[countryCode]
}

const handleFlagError = (event) => {
  const country = event.target?.getAttribute('data-country') || event.target?.dataset?.country
  if (country) {
    flagError.value[country] = true
    flagLoading.value[country] = false
  }
}

// 预加载所有国旗，确认有效的保持显示，无效的显示 fallback
const preloadAllFlags = async () => {
  const countries = new Set([
    ...Object.keys(esimProductList.value || {}),
    ...Object.keys(simProductList.value || {}),
  ])
  if (!countries.size) {
    return
  }

  // 使用 nextTick 确保 DOM 已渲染
  await nextTick()

  // 延迟检查，等待 CSS 加载
  setTimeout(() => {
    countries.forEach((country) => {
      try {
        const flagClass = `flag-icon-${getFlagCode(country).toLowerCase()}`
        
        // 创建一个临时元素来检查 CSS 类是否存在背景图片
        const tempDiv = document.createElement('div')
        tempDiv.className = `flag-icon ${flagClass}`
        tempDiv.style.position = 'absolute'
        tempDiv.style.visibility = 'hidden'
        tempDiv.style.width = '1px'
        tempDiv.style.height = '1px'
        tempDiv.style.top = '-9999px'
        document.body.appendChild(tempDiv)
        
        const computedStyle = window.getComputedStyle(tempDiv)
        const bgImage = computedStyle.backgroundImage
        
        // 清理临时元素
        document.body.removeChild(tempDiv)
        
        // 检查背景图片是否有效
        if (!bgImage || bgImage === 'none' || bgImage === 'url("")') {
          // 没有背景图片，显示 fallback
          flagError.value[country] = true
          flagLoading.value[country] = false
        } else {
          // 有背景图片，尝试验证图片是否真的存在
          const bgUrlMatch = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/)
          if (bgUrlMatch && bgUrlMatch[1]) {
            const testImg = new Image()
            testImg.onload = () => {
              // 图片加载成功，保持显示国旗
              flagError.value[country] = false
              flagLoading.value[country] = false
            }
            testImg.onerror = () => {
              // 图片加载失败，显示 fallback
              flagError.value[country] = true
              flagLoading.value[country] = false
            }
            // 处理相对路径和绝对路径
            let imgSrc = bgUrlMatch[1]
            // flag-icon-css 通常使用相对路径
            if (imgSrc.startsWith('../')) {
              imgSrc = imgSrc.replace('../', '/')
            } else if (imgSrc.startsWith('./')) {
              imgSrc = imgSrc.replace('./', '/')
            } else if (!imgSrc.startsWith('http') && !imgSrc.startsWith('/') && !imgSrc.startsWith('data:')) {
              // 相对路径，尝试常见的 flag-icon-css 路径
              imgSrc = `/node_modules/flag-icon-css/flags/4x3/${getFlagCode(country).toLowerCase()}.svg`
            }
            testImg.src = imgSrc
          } else if (bgImage.includes('data:image')) {
            // data URI，假设有效，保持显示国旗
            flagError.value[country] = false
            flagLoading.value[country] = false
          } else {
            // 其他情况，显示 fallback
            flagError.value[country] = true
            flagLoading.value[country] = false
          }
        }
      } catch (e) {
        // 出错时显示 fallback
        logger.warn(`Failed to check flag for ${country}:`, e)
        flagError.value[country] = true
        flagLoading.value[country] = false
      }
    })
  }, 100) // 减少延迟，更快检查
}

const displayCountryName = (code) => {
  return getCountryRegionDisplayName({
    code,
    locale: locale?.value,
    t,
    te
  })
}

const getFlagCode = (country) => {
  return getFlagCountryCode(country)
}

const isMultiRegionCode = (country) => {
  if (!country) {
    return false
  }
  const raw = String(country).trim().toUpperCase()
  return getFlagCode(raw) === 'UN' && raw !== 'UN'
}

const paymentMessage = () => {
  const status = route.query.status;

  if (status === 'paymentSuccess') {
    const referrer = document.referrer;

    const referrerHostname = new URL(referrer).hostname;
    const allowedReferrerHostname = process.env.VUE_APP_PAYMENT_SUCCESS_REFERRER || 'lksdfjaw.gowisers.com';

    if (referrerHostname === allowedReferrerHostname) {
      showAppNotification({
        message: t('PaymentSuccessful'),
        type: "success",
        duration: 2500,
        position: 'bottom-right'
      });
    }
  }
}


// Switch tab function
const switchTab = async (tab) => {
  hapticFeedback('impact', 'light')
  activeTab.value = tab
  await setTelegramCloudItem(STORAGE_KEYS.HOME_ACTIVE_TAB, tab)
  await nextTick()
  scrollToTop()
  requestAnimationFrame(scrollToTop)
  setTimeout(scrollToTop, 80)
  logger.debug('Switched to tab:', tab)
}

// Go to Package View with scroll to top
const goToPackageView = (country, type = 'eSIM') => {
  hapticFeedback('impact', 'light')
  router.push({ name: 'PackageView', params: { countryCode: country }, query: { type } })
}


// Scroll to top function (Telegram Mini App compatible)
const scrollToTop = () => {
  try {
    resetPageScroll()
  } catch (error) {
    logger.warn('Scroll to top failed:', error);
  }
}



</script>

<style scoped>
.home-tab-switcher {
  background: #f1f2f6;
}

.home-tab-btn {
  flex: 1;
  min-height: 44px;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: color 0.15s ease, background-color 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease;
}

.home-tab-btn:active {
  transform: scale(0.97);
}

.home-tab-btn--active {
  color: #7c3aed;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06), 0 0 0 1px rgba(15, 23, 42, 0.04);
}

:global(html:not(.dark) .home-tab-switcher) {
  border: 1px solid rgba(229, 231, 235, 0.65);
  box-shadow: none;
}

:global(html.dark .home-tab-switcher) {
  background: rgba(31, 41, 55, 0.92);
  border: none;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.22);
}

:global(html.dark .home-tab-btn) {
  color: #9ca3af;
}

:global(html.dark .home-tab-btn--active) {
  color: #a78bfa;
  background: rgba(17, 24, 39, 0.95);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

.home-country-card {
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.12s ease-out;
}

.home-country-card:active {
  transform: scale(0.985);
}

@media (hover: hover) and (pointer: fine) {
  .home-country-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }

  .home-country-card:hover {
    transform: scale(1.02);
    border-color: rgba(139, 92, 246, 0.3);
    box-shadow: 0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.08);
  }

  .home-country-card:hover .home-country-title {
    color: #8b5cf6;
  }

  .home-country-card:hover .home-country-arrow {
    background: rgba(139, 92, 246, 0.1);
  }

  .home-country-card:hover .home-country-arrow-icon {
    color: #8b5cf6;
  }
}

:global(html.dark .home-country-card:hover .home-country-title) {
  color: #c4b5fd;
}

:global(html.dark .home-country-card:hover .home-country-arrow-icon) {
  color: #c4b5fd;
}

@media (prefers-reduced-motion: reduce) {
  .home-country-card,
  .home-tab-btn {
    transition: none;
  }

  .home-country-card:active {
    transform: none;
  }
}

:global(html.dark .home-search-shell--idle:hover) {
  border-color: rgba(55, 65, 81, 0.5) !important;
}

:global(html.dark .home-search-shell--focused) {
  border-color: rgba(139, 92, 246, 0.35) !important;
  box-shadow: none !important;
}

.home-continent-sticky-wrap {
  position: relative;
}

.home-continent-sticky {
  position: relative;
  background: var(--tg-home-page-bg);
  box-sizing: border-box;
}

:global(html.dark .home-continent-sticky) {
  background: var(--tg-home-page-bg);
}

:global(html.dark .home-continent-sticky--pinned) {
  background: var(--tg-home-page-bg);
}

.home-products-loading {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 0.25rem;
}

.home-skeleton-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: 1rem;
  border: 1px solid rgba(229, 231, 235, 0.9);
  background: rgba(255, 255, 255, 0.92);
}

:global(html.dark .home-skeleton-card) {
  border-color: rgba(55, 65, 81, 0.85);
  background: rgba(17, 24, 39, 0.92);
}

.home-skeleton-flag,
.home-skeleton-line {
  background: linear-gradient(90deg, rgba(229, 231, 235, 0.65) 25%, rgba(243, 244, 246, 0.95) 50%, rgba(229, 231, 235, 0.65) 75%);
  background-size: 200% 100%;
  animation: home-skeleton-shimmer 1.2s ease-in-out infinite;
}

:global(html.dark .home-skeleton-flag),
:global(html.dark .home-skeleton-line) {
  background: linear-gradient(90deg, rgba(55, 65, 81, 0.7) 25%, rgba(75, 85, 99, 0.95) 50%, rgba(55, 65, 81, 0.7) 75%);
  background-size: 200% 100%;
}

.home-skeleton-flag {
  width: 4rem;
  height: 3rem;
  border-radius: 0.75rem;
  flex-shrink: 0;
}

.home-skeleton-lines {
  flex: 1;
  min-width: 0;
}

.home-skeleton-line {
  height: 0.75rem;
  border-radius: 9999px;
}

.home-skeleton-line--title {
  width: 42%;
  margin-bottom: 0.625rem;
}

.home-skeleton-line--subtitle {
  width: 28%;
  opacity: 0.75;
}

@keyframes home-skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.home-empty-state {
  min-height: 12rem;
}

.home-company-footer {
  margin-top: 1.5rem;
  padding: 0.75rem 0 0.375rem;
  text-align: center;
}

@media (prefers-reduced-motion: reduce) {
  .home-skeleton-flag,
  .home-skeleton-line {
    animation: none;
  }
}

.home-footer-accent {
  width: 2rem;
  height: 2px;
  margin: 0 auto 0.625rem;
  border-radius: 9999px;
  background: linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.55), transparent);
}

:global(html.dark .home-footer-accent) {
  background: linear-gradient(90deg, transparent, rgba(167, 139, 250, 0.45), transparent);
}

.home-footer-nav {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 0;
}

.home-footer-sep {
  margin: 0 0.375rem;
  font-size: 11px;
  line-height: 1;
  color: #d1d5db;
  user-select: none;
  flex-shrink: 0;
}

:global(html.dark .home-footer-sep) {
  color: #4b5563;
}

.home-footer-link {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0.125rem 0;
  font-size: 11px;
  line-height: 1.3;
  font-weight: 600;
  color: #6b7280;
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.home-footer-link:active,
.home-footer-link.router-link-active {
  color: #7c3aed;
}

@media (hover: hover) and (pointer: fine) {
  .home-footer-link:hover {
    color: #7c3aed;
  }
}

:global(html.dark .home-footer-link) {
  color: #9ca3af;
}

:global(html.dark .home-footer-link:active),
:global(html.dark .home-footer-link.router-link-active) {
  color: #c4b5fd;
}

@media (hover: hover) and (pointer: fine) {
  :global(html.dark .home-footer-link:hover) {
    color: #c4b5fd;
  }
}

.home-footer-meta {
  margin-top: 0.5rem;
  font-size: 10px;
  line-height: 1.4;
  letter-spacing: 0.02em;
  color: #9ca3af;
}

:global(html.dark .home-footer-meta) {
  color: #6b7280;
}

.home-search-shell {
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(229, 231, 235, 0.95);
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

@media (hover: hover) and (pointer: fine) {
  .home-search-shell--idle:hover {
    border-color: rgba(209, 213, 219, 0.95);
  }
}

.home-search-shell--focused {
  background: #ffffff;
  border-color: rgba(139, 92, 246, 0.28);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
}

.home-search-clear {
  position: absolute;
  top: 50%;
  right: 8px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 9999px;
  color: #9ca3af;
  background: rgba(107, 114, 128, 0.1);
  cursor: pointer;
  transform: translateY(-50%);
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.home-search-clear:active {
  transform: translateY(-50%) scale(0.94);
  color: #6b7280;
  background: rgba(107, 114, 128, 0.18);
}

.home-search-clear-icon {
  width: 11px;
  height: 11px;
  pointer-events: none;
}

:global(html.dark .home-search-clear) {
  color: #9ca3af;
  background: rgba(156, 163, 175, 0.14);
}

:global(html.dark .home-search-clear:active) {
  color: #d1d5db;
  background: rgba(156, 163, 175, 0.24);
}

.home-search-input {
  display: block;
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  margin: 0;
  padding: 0 36px 0 38px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #374151;
  caret-color: #8b5cf6;
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  line-height: 38px;
  letter-spacing: -0.01em;
  -webkit-font-smoothing: antialiased;
  -webkit-appearance: none;
  appearance: none;
  outline: none;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  transition: box-shadow 0.15s ease;
}

.home-search-input:focus {
  box-shadow: none;
}

.home-search-input::placeholder {
  color: #9ca3af;
  font-weight: 400;
  opacity: 1;
}

:global(html.dark .home-search-shell) {
  background: rgba(17, 24, 39, 0.92) !important;
  border: 1px solid rgba(55, 65, 81, 0.45) !important;
  box-shadow: none !important;
}

:global(html.dark .home-search-input) {
  background: transparent !important;
  color: #e5e7eb !important;
  caret-color: #a78bfa !important;
}

:global(html.dark .home-search-input::placeholder) {
  color: #9ca3af !important;
  font-weight: 400;
}

:global(html.dark .home-search-icon) {
  color: #9ca3af;
}

/* Campaign poster scroll area */
.campaign-poster-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(139, 92, 246, 0.35) transparent;
}

.campaign-poster-scroll::-webkit-scrollbar {
  width: 4px;
}

.campaign-poster-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.campaign-poster-scroll::-webkit-scrollbar-thumb {
  background: rgba(139, 92, 246, 0.35);
  border-radius: 2px;
}

.campaign-dialog-panel {
  animation: campaignDialogIn 0.22s ease-out;
}

.campaign-footer {
  border-top: 1px solid #f3f4f6;
  background: linear-gradient(180deg, #ffffff 0%, #fafafa 100%);
}

:global(html.dark .campaign-footer) {
  border-top-color: #374151;
  background: linear-gradient(180deg, #1f2937 0%, #111827 100%);
}

:global(html.dark .campaign-btn-ghost) {
  border-color: #4b5563;
  color: #d1d5db;
  background: #374151;
}

.campaign-btn {
  border-radius: 10px;
  font-size: 12px;
  line-height: 1;
  padding: 9px 12px;
  min-height: 34px;
  transition: background-color 0.12s ease, transform 0.12s ease, filter 0.12s ease;
  -webkit-tap-highlight-color: transparent;
}

.campaign-btn:active {
  transform: scale(0.98);
}

.campaign-btn-ghost {
  border: 1px solid #e5e7eb;
  color: #4b5563;
  background: #fff;
}

.campaign-btn-ghost:active {
  background: #f3f4f6;
}

.campaign-btn-primary {
  border: 1px solid transparent;
  color: #fff;
  background: linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%);
  box-shadow: 0 8px 16px -10px rgba(124, 58, 237, 0.95);
}

.campaign-btn-primary:active {
  filter: brightness(0.96);
}

@media (hover: hover) and (pointer: fine) {
  .campaign-btn {
    transition: all 0.18s ease;
  }

  .campaign-btn-ghost:hover {
    background: #f9fafb;
  }

  .campaign-btn-primary:hover {
    filter: brightness(1.02);
  }

  :global(html.dark .campaign-btn-ghost:hover) {
    background: #4b5563;
  }
}

.campaign-footer-spacer {
  flex: 1;
}

.mobile-frame-fixed {
  padding-left: 0;
  padding-right: 0;
}

.install-banner {
  width: 100%;
  max-width: none;
  margin: 0;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 10px 12px;
}

.install-banner-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.install-banner-icon-text {
  display: none;
}

.install-banner-logo {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  display: flex;
  object-fit: cover;
}

.install-banner-content {
  min-width: 0;
  flex: 1;
}

.install-banner-title {
  font-size: 13px;
  line-height: 1.25;
  color: #1f2937;
}

.install-banner-subtitle {
  font-size: 12px;
  line-height: 1.2;
  color: #4b5563;
  margin-top: 2px;
}

.install-banner-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.install-btn {
  border-radius: 8px;
  border: 1px solid transparent;
  min-height: 30px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 600;
  -webkit-tap-highlight-color: transparent;
}

.install-btn-ghost:active {
  background: #e5e7eb;
}

.install-btn-primary {
  color: #ffffff;
}

.install-btn-ghost {
  color: #374151;
  background: #f3f4f6;
  border-color: #e5e7eb;
}

.install-guide-dialog {
  animation: campaignDialogIn 0.22s ease-out;
}

.install-guide-header {
  height: 64px;
  padding: 0 16px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--el-color-primary, #8b5cf6) !important;
}

.install-guide-title {
  color: #ffffff;
  font-size: 20px;
  line-height: 1;
  font-weight: 700;
}

.install-guide-close {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  border-radius: 9999px;
  border: 1.5px solid rgba(255, 255, 255, 0.9);
  color: #ffffff;
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.install-guide-content {
  padding: 14px;
  background: #ffffff;
}

.install-guide-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 10px;
}

.install-guide-step {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.install-guide-media {
  position: relative;
  padding-bottom: 12px;
}

.install-guide-image {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.install-guide-index {
  position: absolute;
  left: 50%;
  bottom: 3px;
  transform: translate(-50%, 20%);
  width: 22px !important;
  height: 22px !important;
  border-radius: 9999px;
  background: var(--el-color-primary, #8b5cf6) !important;
  color: #ffffff;
  font-size: 11px !important;
  line-height: 1;
  font-family: Arial, sans-serif;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.install-guide-caption {
  font-size: 14px;
  line-height: 1.4;
  color: #111827;
  font-weight: 400;
  text-align: center;
}

:global(html.dark .install-banner) {
  background: #1f2937;
  border-color: #374151;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.28);
}

:global(html.dark .install-banner-icon) {
  background: #374151;
}

:global(html.dark .install-banner-title) {
  color: #f3f4f6;
}

:global(html.dark .install-banner-subtitle) {
  color: #9ca3af;
}

:global(html.dark .install-btn-ghost) {
  color: #d1d5db;
  background: #374151;
  border-color: #4b5563;
}

:global(html.dark .install-guide-content) {
  background: #111827;
}

:global(html.dark .install-guide-image) {
  border-color: #374151;
}

:global(html.dark .install-guide-caption) {
  color: #e5e7eb;
}

:global(html.dark ::-webkit-scrollbar-track) {
  background: #1f2937;
}

:global(html.dark ::-webkit-scrollbar-thumb) {
  background: #4b5563;
}

:global(html.dark ::-webkit-scrollbar-thumb:hover) {
  background: #6b7280;
}

@keyframes campaignDialogIn {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Custom flag icon styles */
.flag-icon {
  background-size: contain !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
  min-width: 64px;
  min-height: 48px;
}

/* Smooth transitions for interactive elements (exclude text inputs — caret glitches on iOS) */
button,
a,
[role="button"] {
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}


/* Telegram Mini App optimizations */
button, [role="button"] {
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
}

/* Prevent text selection on interactive elements */
.group, .cursor-pointer {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Optimize touch targets */
@media (max-width: 768px) {
  button, [role="button"], .cursor-pointer {
    min-height: 44px;
    min-width: 44px;
  }
}
</style>
